'use client'
import { cn } from '@/lib/utils'
import { Dialog } from '@headlessui/react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { Dispatch, SetStateAction } from 'react'
import { GeneralModalFlow } from '@/components/modals/general-modal-flow'

export default function UpgradePopup({
  isOpen,
  setIsOpen,
  title,
  location,
  listing,
  isEnhanced,
}: {
  isOpen: boolean
  title: string
  location: string
  listing: any
  isEnhanced?: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
}) {
  const { push } = useRouter()

  const isMarket = listing?.user_type?.toLowerCase() === 'market'

  const bullets = isMarket ? [
    'Showcase your vendors and seasonal lineup',
    'Get discovered by local food lovers searching nearby',
    'Connect with producers looking for market opportunities',
  ] : [
    'Share your products and seasonal availability',
    'Get discovered by local food lovers searching nearby',
    'Connect with farmers markets looking for vendors like you',
  ]

  return (
    <Dialog
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4'
      )}
      open={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Dialog.Panel
        className={cn(
          'relative w-[70vw] max-w-[930px] rounded-[24px] border-[4px] border-white bg-white shadow-2xl md:w-[90vw] lg:w-[80vw] xl:w-[70vw] 2xl:w-[60%] xxs:w-[80vw]'
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="flex flex-col items-start justify-center gap-4 px-10 py-10 md:pl-14 xxs:px-6">
            <p className="text-sm font-medium text-[#767A85]">
              We have limited information for this {isMarket ? 'market' : 'farm'}
            </p>
            <h1 className="text-[28px] font-semibold leading-tight tracking-tight md:text-[34px] xxxs:text-[24px]">
              Is <span className="text-[#85C349]">{title}</span> yours?
            </h1>
            <div className="flex w-fit items-center gap-4 rounded-lg bg-[#F3FAEB] p-4">
              <Image
                src={
                  !isEnhanced || !listing
                    ? '/assets/images/profile.svg'
                    : listing.photo || '/assets/images/profile.svg'
                }
                alt="business"
                width={40}
                height={40}
                className="h-[38px] w-[38px] rounded-full"
              />
              <div>
                <p className="text-lg font-bold xxxs:text-sm">{title}</p>
                <p className="font-medium text-[#1F2026] xxxs:mt-2 xxxs:text-xs">
                  {location}
                </p>
              </div>
            </div>
            <ul className="flex flex-col gap-2">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-[#767A85]">
                  <span className="mt-0.5 text-[#85C349]">✓</span>
                  {b}
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-2">
              <GeneralModalFlow btnTitle={'Complete this Listing'} />
              <p className="text-xs text-[#767A85] text-center">Free service · Takes 2 minutes</p>
            </div>
            <button
              onClick={() => push('/homecooks')}
              className="text-sm text-[#548828] underline hover:opacity-75 focus:outline-none"
            >
              Just browsing? Join free as a home cook →
            </button>
          </div>
          <div className="relative hidden md:block min-h-[420px] rounded-r-[20px] overflow-hidden">
            <Image
              src={'/images/yourBusiness.png'}
              alt="yourBusiness"
              fill
              className="object-cover object-center"
            />
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  )
}
